import React, { useState, useEffect } from 'react';
import { Card, Form } from 'react-bootstrap';
import SubmitButton from './SubmitButton';
import styles from './CardForm.module.scss';
import Select from 'react-select'; // <- Importa o Select do react-select
import PhoneNumberInput from './PhoneNumberInput';
import { Link, useNavigate } from 'react-router-dom';

type Field = {
  label: string;
  name: string;
  type: 'text' | 'email' | 'tel' | 'textarea' | 'select' | 'checkbox' | 'number' | 'radio' | 'multiselect' | 'password' | 'hidden';
  required?: boolean;
  disabled?: boolean;
  maxLength?: number;
  rows?: number;
  options?: { label: string; value: string }[];
  format?: (value: string) => string;
};

type CardFormProps = {
  id?: string; // <- Nova prop opcional
  title: string;
  action: string;
  method: 'GET' | 'POST';
  fields: Field[];
  link?: { label: string; url: string };
  defaultValues?: Record<string, any>;
  submitLabel?: string;
  submittingLabel?: string; // <- Nova prop opcional
  onSubmit?: (
    formData: Record<string, any>,
    action: string,
    method: string
  ) => Promise<{ errorCode: number; message: string; redirect?: string }> | void;
};



const CardForm: React.FC<CardFormProps> = ({ id, title, action, method, fields, submitLabel, submittingLabel, defaultValues, onSubmit, link }) => {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [message, setMessage] = useState<string>('');
  const [showMessage, setShowMessage] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<any>) => {
    const { name, value, checked } = e.target;

    const field = fields.find(f => f.name === name);

    const formattedValue = field?.format ? field.format(value) : value;

    if (field?.type === 'multiselect') {
      setFormData(prev => {
        const currentValues: string[] = prev[name] || [];
        return {
          ...prev,
          [name]: checked
            ? [...currentValues, value]
            : currentValues.filter(v => v !== value),
        };
      });
    } else if (field?.type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: formattedValue,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (onSubmit) {
        const response = await onSubmit(formData, action, method); // <- usa função externa
        if (response) {
          if (response.errorCode !== 0) throw new Error(response.message);
          setMessage(response.message || 'Formulário enviado com sucesso!');
          setIsError(false);
          if (response.redirect) {
            setShowMessage(false); // <- oculta a mensagem de sucesso
            navigate(response.redirect); // <- redireciona para a URL fornecida
            return; // <- interrompe a execução após o redirecionamento
          }
        } else {
          setMessage('Formulário enviado com sucesso!');
          setIsError(false);
        }
        setFormData({});
      } else {
        // <- comportamento padrão (form encoded + fetch)
        const body = new URLSearchParams();
        for (const key in formData) {
          const value = formData[key];
          if (Array.isArray(value)) {
            value.forEach(v => body.append(key, v));
          } else {
            body.append(key, value);
          }
        }

        const response = await fetch(action, {
          method,
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: method === 'POST' ? body.toString() : undefined,
        });

        if (!response.ok) throw new Error('Falha no envio');

        setMessage('Formulário enviado com sucesso!');
        setIsError(false);
        setFormData({});
      }
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
      setIsError(true);
      console.error(err);
      //alert('Erro ao enviar o formulário. Tente novamente mais tarde.');
    } finally {
      if (showMessage)
        setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (defaultValues) {
      setFormData(defaultValues);
    }
  }, [defaultValues]);


  return (
    <Card className={styles["card"]} id={id}>
      <Card.Body className={styles["card-body"]}>
        <h3 className={[styles["card-title"], styles["center"]].join(' ')}>{title}</h3>
        <Form onSubmit={handleSubmit} id={`form-${id}`}>
          {fields.map((field, idx) => (
            <div className="mb-3" key={idx}>
              <Form.Label htmlFor={field.name} className={field.type === 'hidden' ? 'd-none' : ''}>{field.label}</Form.Label>

              {field.type === 'textarea' ? (
                <Form.Control
                  as="textarea"
                  id={field.name}
                  name={field.name}
                  required={field.required}
                  disabled={field.disabled}
                  rows={field.rows || 3}
                  value={formData[field.name] || ''}
                  onChange={handleChange}
                />
              ) : field.type === 'select' ? (
                <Select
                  id={field.name}
                  name={field.name}
                  options={field.options}
                  onChange={(selectedOption) => {
                    setFormData(prev => ({
                      ...prev,
                      [field.name]: selectedOption?.value || '',
                    }));
                  }}
                  value={field.options?.find(opt => opt.value === formData[field.name])}
                  isDisabled={field.disabled}
                  isClearable={true}
                />
              ) : field.type === 'radio' ? (
                field.options?.map((opt, i) => (
                  <Form.Check
                    key={i}
                    type="radio"
                    label={opt.label}
                    name={field.name}
                    value={opt.value}
                    checked={formData[field.name] === opt.value}
                    onChange={handleChange}
                    disabled={field.disabled}
                  />
                ))
              ) : field.type === 'hidden' ? (
                <input type="hidden" name={field.name} value={formData[field.name] || ''} />
              ) : field.type === 'multiselect' ? (
                field.options?.map((opt, i) => (
                  <Form.Check
                    key={i}
                    type="checkbox"
                    label={opt.label}
                    name={field.name}
                    value={opt.value}
                    checked={(formData[field.name] || []).includes(opt.value)}
                    onChange={handleChange}
                    disabled={field.disabled}
                  />
                ))
              ) : field.type === 'password' ? (
                <Form.Control
                  id={field.name}
                  name={field.name}
                  type={field.type}
                  required={field.required}
                  disabled={field.disabled}
                  maxLength={field.maxLength}
                  value={formData[field.name] || ''}
                  onChange={handleChange}
                />
              ) : field.type === 'tel' ? (
                <PhoneNumberInput
                  id={field.name}
                  defaultValue={formData[field.name] || ''}
                  className={styles["phone-field"]}
                  required={field.required}
                  disabled={field.disabled}
                />
              ) : (
                <Form.Control
                  id={field.name}
                  name={field.name}
                  type={field.type}
                  required={field.required}
                  disabled={field.disabled}
                  maxLength={field.maxLength}
                  value={field.type === 'checkbox' ? undefined : formData[field.name] || ''}
                  checked={field.type === 'checkbox' ? formData[field.name] || false : undefined}
                  onChange={handleChange}
                />
              )}
            </div>
          ))}
          <SubmitButton disabled={isSubmitting}>
            {isSubmitting ? (submittingLabel || 'Enviando...') : (submitLabel || 'Enviar')}
          </SubmitButton>
          {message && (
            showMessage && (
              <div className="mt-3">
                {isError ? (
                  <div className="alert alert-danger" role="alert">
                    {message}
                  </div>
                ) : (
                  <div className="alert alert-success" role="alert">
                    {message}
                  </div>
                )}
              </div>
            )
          )}


          {/* Link opcional abaixo dos campos */}
          {link && (
            <div className="mt-3">
              <Link to={link.url} className={styles["form-link"]}>
                {link.label}
              </Link>
            </div>
          )}
        </Form>
      </Card.Body>
    </Card>
  );
};

export default CardForm;
